/**
 * Collapses internal whitespace and trims the string. Returns undefined for empty inputs.
 */
export function collapseWhitespace(value?: string | null): string | undefined {
  if (!value) return undefined;
  const cleaned = value.replace(/\s+/g, " ").trim();
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Normalizes a full name string into an array of parts (given names) by splitting on spaces.
 */
export function normalizeNameParts(nameString?: string): string[] | undefined {
  const normalized = collapseWhitespace(nameString);
  return normalized ? normalized.split(" ").filter(Boolean) : undefined;
}

/**
 * Input payload for full-name validation to keep UI and API checks consistent.
 */
interface FullNameValidationInput {
  name: string;
}

/**
 * Result details for full-name validation outcomes used across the app.
 */
interface FullNameValidationResult {
  isNameEmpty: boolean;
  isValidFullName: boolean;
}

/**
 * Determines whether a name includes at least two parts after whitespace normalization.
 */
export function getFullNameValidation({ name }: FullNameValidationInput): FullNameValidationResult {
  const nameParts = normalizeNameParts(name) ?? [];
  const isNameEmpty = nameParts.length === 0;
  const isValidFullName = nameParts.length >= 2;

  return { isNameEmpty, isValidFullName };
}

/**
 * Normalizes a string for accent-insensitive and symbol-tolerant search.
 * - Lowercases
 * - Removes diacritics (accents) via NFD normalization
 * - Strips punctuation/symbols, retaining only letters, numbers and spaces
 * - Collapses repeated whitespace
 */
export function normalizeForSearch(input: string | undefined | null): string {
  if (!input) return "";
  const lower = input.toLowerCase();
  // Remove diacritics using NFD + Unicode combining mark class
  // biome-ignore lint/complexity/useRegexLiterals: \p{} literal with /u triggers TS1501 under target es5; RegExp ctor evades it
  const noDiacritics = lower.normalize("NFD").replace(new RegExp("\\p{M}+", "gu"), "");
  // Preserve all Unicode letters/numbers, convert punctuation/symbols to spaces to keep token boundaries
  const lettersNumbersAndSpaces = noDiacritics.replace(
    // biome-ignore lint/complexity/useRegexLiterals: \p{} literal with /u triggers TS1501 under target es5; RegExp ctor evades it
    new RegExp("[^\\p{L}\\p{N}\\s]+", "gu"),
    " ",
  );
  return lettersNumbersAndSpaces.replace(/\s+/g, " ").trim();
}

/**
 * Splits a token into words, treating whitespace, `_`, and `-` as separators
 * and dropping empty segments. Building block for display formatting — not for
 * comparing machine-readable keys, which should be matched exactly.
 */
function splitIntoWords(value: string): string[] {
  return value.split(/[\s_-]+/).filter(Boolean);
}

/**
 * Capitalizes the first character, leaving the rest of the string untouched.
 * `capitalize("scheduled") === "Scheduled"`; `capitalize("on hold") === "On hold"`.
 * Use this for a single token or an already-spaced phrase; for per-word Title
 * Case use {@link toTitleCase}.
 */
export function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Renders free-form text as Title Case: splits on whitespace / `_` / `-`,
 * lowercases each word, then capitalizes its first letter.
 * `toTitleCase("partial_results") === "Partial Results"`; `toTitleCase("on-hold") === "On Hold"`.
 *
 * NOT for humanizing a finite code set (FHIR statuses, enum values, slugs) — map
 * those to labels with an explicit `Record`/`Map` instead; mechanical title-casing
 * mis-renders codes like `"entered-in-error"` and breaks on unmapped values. See
 * "String Handling" in the root AGENTS.md.
 * For camelCase identifiers use `camelCaseToTitleCase` from `@/lib/utils`.
 */
export function toTitleCase(value: string): string {
  return splitIntoWords(value)
    .map((word) => capitalize(word.toLowerCase()))
    .join(" ");
}

/**
 * Canonicalizes a tax ID (EIN) by removing all non-digit characters.
 * Returns an empty string for empty/null inputs.
 * This ensures consistent storage regardless of input format (e.g., "12-3456789" → "123456789").
 */
export function canonicalizeTaxId(value?: string | null): string {
  if (!value) return "";
  return value.replace(/\D/g, "");
}

/**
 * Escapes text for safe interpolation into HTML content or a double-quoted attribute value.
 * DOM-free (regex-based) - safe to use in server-side code that hand-builds HTML strings without
 * a browser DOM available (e.g. AI generators), unlike TipTap's `generateHTML`/`generateJSON`.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// tags that end a line of text - a heading, a paragraph, a list item, a table row
const BLOCK_BREAK_TAG_PATTERN = /<(?:br\s*\/?|\/(?:p|div|li|h[1-6]|tr|blockquote|pre))>/gi;

/**
 * Strips HTML tags, leaving plain text - not full sanitization, just enough to turn rendered
 * HTML (e.g. a NoteTemplate block's `.html`) back into readable text for an LLM prompt. DOM-free
 * (regex-based), safe for server-side code with no browser DOM available.
 *
 * A block tag becomes a line break, so headings and list items stay on their own lines. An
 * inline tag (`<strong>`, `<em>`, `<a>`) is removed with no space, so `hyper<b>tension</b>`
 * stays one word. The common entities are decoded back to text.
 */
export function stripHtmlTags(html: string): string {
  const text = html
    .replace(BLOCK_BREAK_TAG_PATTERN, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    // `&amp;` last - so an escaped entity such as `&amp;lt;` reads as `&lt;`, not as `<`
    .replace(/&amp;/g, "&");

  return text
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n");
}

/**
 * Trims a string. Returns undefined for non-string or blank input.
 */
export function trimToUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

/**
 * Keeps the first `maxLength` characters of human text and appends "…" when it cut anything.
 * Counts graphemes (what a reader sees as one character), so an emoji, a flag, or an accented
 * letter is never split. Trailing whitespace before the ellipsis is dropped.
 */
export function truncateWithEllipsis(value: string, maxLength: number): string {
  const characters = Array.from(graphemeSegmenter.segment(value), ({ segment }) => segment);
  if (characters.length <= maxLength) return value;
  return `${characters.slice(0, maxLength).join("").trimEnd()}…`;
}
